import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

type Customer = { name: string; site_count: number };
type Row = {
  site_id: string;
  site_name: string;
  atg_id: string;
  atg_label: string;
  tank_number: number;
  tank_label: string | null;
  product_code: string | null;
  volume: number | null;
  tc_volume: number | null;
  ullage: number | null;
  height: number | null;
  water_height: number | null;
  temperature: number | null;
  collected_at: string;
};

type SortKey = 'site_name' | 'atg_label' | 'tank_number' | 'tank_label'
             | 'product_code' | 'volume' | 'tc_volume' | 'ullage'
             | 'height' | 'water_height' | 'temperature' | 'collected_at';
type SortDir = 'asc' | 'desc';

const COLS: Array<{ key: SortKey; label: string; align?: 'right'; format?: (r: Row) => any }> = [
  { key: 'site_name',    label: 'Site' },
  { key: 'atg_label',    label: 'ATG' },
  { key: 'tank_number',  label: 'Tank #', align: 'right' },
  { key: 'tank_label',   label: 'Tank name' },
  { key: 'product_code', label: 'Product' },
  { key: 'volume',       label: 'Volume (gal)',    align: 'right', format: r => r.volume?.toFixed?.(1) },
  { key: 'tc_volume',    label: 'TC Volume (gal)', align: 'right', format: r => r.tc_volume?.toFixed?.(1) },
  { key: 'ullage',       label: 'Ullage (gal)',    align: 'right', format: r => r.ullage?.toFixed?.(1) },
  { key: 'height',       label: 'Height (in)',     align: 'right', format: r => r.height?.toFixed?.(2) },
  { key: 'water_height', label: 'Water (in)',      align: 'right', format: r => r.water_height?.toFixed?.(2) },
  { key: 'temperature',  label: 'Temp (°F)',       align: 'right', format: r => r.temperature?.toFixed?.(1) },
  { key: 'collected_at', label: 'Collected', format: r => new Date(r.collected_at).toLocaleString() },
];

type ViewMode = 'table' | 'gauges';

export function Inventory() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customer, setCustomer] = useState<string>('');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [view, setView] = useState<ViewMode>('table');
  // Default sort: most-empty tanks (highest ullage) first — useful for "what needs filling?".
  const [sortKey, setSortKey] = useState<SortKey>('ullage');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    api.get('/api/customers').then((c: Customer[]) => {
      setCustomers(c);
      if (c.length && !customer) setCustomer(c[0].name);
    }).catch(() => {});
  }, []);

  async function load(c: string) {
    if (!c) return;
    setLoading(true); setErr('');
    try {
      const data = await api.get(`/api/inventory?customer=${encodeURIComponent(c)}`);
      setRows(data);
    } catch (e: any) {
      setErr(e.message || 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(customer); }, [customer]);

  // Sort client-side. Null/undefined values sort to the bottom regardless of direction
  // so empty cells don't crowd the top of "smallest first" views.
  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const av = (a as any)[sortKey];
      const bv = (b as any)[sortKey];
      const aNull = av === null || av === undefined;
      const bNull = bv === null || bv === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      let cmp: number;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, sortKey, sortDir]);

  // Totals row — sums of volume / TC / ullage across visible tanks.
  const totals = useMemo(() => {
    const sum = (k: keyof Row) => rows.reduce((acc, r) => acc + (Number(r[k]) || 0), 0);
    return {
      volume: sum('volume'),
      tc_volume: sum('tc_volume'),
      ullage: sum('ullage'),
      count: rows.length,
    };
  }, [rows]);

  function clickHeader(k: SortKey) {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'tank_number' || k === 'site_name' || k === 'atg_label' ? 'asc' : 'desc'); }
  }

  function arrow(k: SortKey) {
    if (sortKey !== k) return <span style={{ color: 'var(--muted)', marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  function downloadCsv() {
    const header = COLS.map(c => c.label);
    const lines = [header.join(',')];
    for (const r of sorted) {
      lines.push(COLS.map(c => csvCell(c.format ? c.format(r) : (r as any)[c.key])).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-${customer.replace(/[^a-z0-9]+/gi, '_')}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div className="card">
        <h2>Customer inventory</h2>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div className="col">
            <label>Customer</label>
            <select value={customer} onChange={e => setCustomer(e.target.value)}>
              {customers.length === 0 && <option value="">No customers yet</option>}
              {customers.map(c => (
                <option key={c.name} value={c.name}>{c.name} ({c.site_count} site{c.site_count === 1 ? '' : 's'})</option>
              ))}
            </select>
          </div>
          <div className="col">
            <label>View</label>
            <div style={{ display: 'flex', gap: 0, border: '1px solid var(--muted)', borderRadius: 4, overflow: 'hidden', width: 'fit-content' }}>
              <button onClick={() => setView('table')}
                      style={{ borderRadius: 0, border: 'none', padding: '6px 14px',
                               background: view === 'table' ? '#2e7df6' : 'transparent',
                               color: view === 'table' ? '#fff' : 'inherit' }}>
                Table
              </button>
              <button onClick={() => setView('gauges')}
                      style={{ borderRadius: 0, border: 'none', padding: '6px 14px',
                               background: view === 'gauges' ? '#2e7df6' : 'transparent',
                               color: view === 'gauges' ? '#fff' : 'inherit' }}>
                Gauges
              </button>
            </div>
          </div>
          <div className="col" style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <button onClick={() => load(customer)} disabled={!customer || loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button onClick={downloadCsv} disabled={!sorted.length}>Download CSV</button>
          </div>
        </div>
        {err && <div style={{ color: 'crimson', marginTop: 8 }}>{err}</div>}
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h3 style={{ margin: 0 }}>
            {totals.count} tank{totals.count === 1 ? '' : 's'} across {new Set(rows.map(r => r.site_id)).size} site{new Set(rows.map(r => r.site_id)).size === 1 ? '' : 's'}
          </h3>
          <div style={{ color: 'var(--muted)', fontSize: 14 }}>
            Totals: <b>{totals.volume.toFixed(0)}</b> gal volume ·{' '}
            <b>{totals.tc_volume.toFixed(0)}</b> gal TC ·{' '}
            <b>{totals.ullage.toFixed(0)}</b> gal ullage
          </div>
        </div>
        <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4, marginBottom: 8 }}>
          {view === 'table'
            ? 'Click any column header to sort. Click again to reverse.'
            : 'Each site is one row. Fill level reflects volume / (volume + ullage). Hover a circle for details.'}
        </div>

        {view === 'gauges' ? <GaugesView rows={sorted} /> : (
        <div className="table-wrap"><table>
          <thead>
            <tr>
              {COLS.map(c => (
                <th key={c.key}
                    onClick={() => clickHeader(c.key)}
                    style={{ cursor: 'pointer', textAlign: c.align ?? 'left', userSelect: 'none' }}>
                  {c.label}{arrow(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={`${r.atg_id}-${r.tank_number}-${i}`}>
                {COLS.map(c => (
                  <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                    {c.key === 'site_name'
                      ? <Link to={`/sites/${r.site_id}`}>{r.site_name}</Link>
                      : c.format ? (c.format(r) ?? <span style={{ color: 'var(--muted)' }}>—</span>)
                                 : ((r as any)[c.key] ?? <span style={{ color: 'var(--muted)' }}>—</span>)}
                  </td>
                ))}
              </tr>
            ))}
            {!sorted.length && !loading && (
              <tr><td colSpan={COLS.length} style={{ color: 'var(--muted)' }}>
                No inventory readings for this customer yet. Polls land within ~5 minutes of an ATG being added.
              </td></tr>
            )}
          </tbody>
        </table></div>
        )}
      </div>
    </>
  );
}

/**
 * Gauges view — one row per site, all tanks on the same line as filled circles.
 * Fill % = volume / (volume + ullage). Color shifts red → amber → green as
 * fill increases so a sweeping glance across a customer's stations highlights
 * who needs a delivery soonest.
 */
function GaugesView({ rows }: { rows: Row[] }) {
  // Group by site, preserving incoming sort order for tanks within a site.
  const groups = useMemo(() => {
    const m = new Map<string, { siteId: string; siteName: string; tanks: Row[] }>();
    for (const r of rows) {
      const g = m.get(r.site_id) ?? { siteId: r.site_id, siteName: r.site_name, tanks: [] };
      g.tanks.push(r);
      m.set(r.site_id, g);
    }
    // Within a site, always sort tanks by tank_number so the layout is stable
    // regardless of the global sort key.
    for (const g of m.values()) g.tanks.sort((a, b) => a.tank_number - b.tank_number);
    return Array.from(m.values()).sort((a, b) => a.siteName.localeCompare(b.siteName));
  }, [rows]);

  if (!groups.length) {
    return <div style={{ color: 'var(--muted)' }}>No inventory readings for this customer yet.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {groups.map(g => {
        const siteVol = g.tanks.reduce((s, t) => s + (Number(t.volume) || 0), 0);
        const siteCap = g.tanks.reduce((s, t) => s + (Number(t.volume) || 0) + (Number(t.ullage) || 0), 0);
        const sitePct = siteCap ? (siteVol / siteCap) * 100 : null;
        return (
          <div key={g.siteId}
               style={{ display: 'flex', alignItems: 'center', gap: 24,
                        padding: 16, border: '1px solid rgba(128,128,128,0.2)', borderRadius: 6 }}>
            <div style={{ minWidth: 200 }}>
              <Link to={`/sites/${g.siteId}`} style={{ fontWeight: 600, fontSize: 15 }}>
                {g.siteName}
              </Link>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>
                {g.tanks.length} tank{g.tanks.length === 1 ? '' : 's'}
                {sitePct != null && <> · {sitePct.toFixed(0)}% full</>}
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                {siteVol.toFixed(0)} / {siteCap.toFixed(0)} gal
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {g.tanks.map(t => <TankGauge key={`${t.atg_id}-${t.tank_number}`} t={t} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Single tank rendered as a filled circle (donut fill from the bottom).
 * Uses two stacked SVG circles: a grey track + a rotated stroke arc whose
 * stroke-dasharray encodes the fill percentage. Tank label / product /
 * gallons / fill % sit underneath.
 */
function TankGauge({ t }: { t: Row }) {
  const vol = Number(t.volume) || 0;
  const ull = Number(t.ullage) || 0;
  const cap = vol + ull;
  const pct = cap > 0 ? (vol / cap) * 100 : 0;
  const color = fillColor(pct);
  const R = 38;          // circle radius
  const STROKE = 10;     // ring thickness
  const C = 2 * Math.PI * R;
  const dash = (pct / 100) * C;
  const SIZE = (R + STROKE) * 2;
  const title = `${t.site_name} — ${t.atg_label}\n` +
                `Tank ${t.tank_number}${t.tank_label ? ` (${t.tank_label})` : ''}\n` +
                `Product: ${t.product_code ?? '—'}\n` +
                `Volume: ${vol.toFixed(1)} gal\n` +
                `Ullage: ${ull.toFixed(1)} gal\n` +
                `Fill:   ${pct.toFixed(1)}%\n` +
                `Temp:   ${t.temperature?.toFixed?.(1) ?? '—'} °F\n` +
                `Water:  ${t.water_height?.toFixed?.(2) ?? '—'} in`;

  return (
    <div style={{ width: 110, textAlign: 'center' }} title={title}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ display: 'block', margin: '0 auto' }}>
        {/* Background track */}
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R}
                fill="none" stroke="rgba(128,128,128,0.25)" strokeWidth={STROKE} />
        {/* Fill arc — rotated -90deg so it starts from the top */}
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R}
                fill="none" stroke={color} strokeWidth={STROKE} strokeLinecap="round"
                strokeDasharray={`${dash} ${C - dash}`}
                transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`} />
        {/* Center label: percent */}
        <text x={SIZE / 2} y={SIZE / 2 + 5} textAnchor="middle"
              fontSize="16" fontWeight="600" fill="currentColor">
          {pct.toFixed(0)}%
        </text>
      </svg>
      <div style={{ fontSize: 12, marginTop: 6, fontWeight: 600 }}>
        Tank {t.tank_number}{t.product_code ? ` (${t.product_code})` : ''}
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)', minHeight: 14 }}>
        {t.tank_label || '—'}
      </div>
      <div style={{ fontSize: 11, marginTop: 2 }}>
        {vol.toFixed(0)} gal
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
        {ull.toFixed(0)} ullage
      </div>
    </div>
  );
}

// Red below 20%, amber 20–50%, green above 50%. Gradient blends within bands.
function fillColor(pct: number): string {
  if (pct < 20) return '#d63b5b';
  if (pct < 50) return '#e0853d';
  return '#28a76f';
}

function csvCell(v: any): string {
  if (v == null) return '';
  const s = typeof v === 'number' ? String(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
